(() => {
  const initializeDiscount = (root) => {
    if (root.dataset.comelyDiscountReady === 'true') return;
    root.dataset.comelyDiscountReady = 'true';

    const dialog = root.querySelector('[data-comely-discount-dialog]');
    const closeButton = root.querySelector('[data-comely-discount-close]');
    const claimButton = root.querySelector('[data-comely-offer-claim]');
    const shippingButton = root.querySelector('[data-comely-offer-shipping]');
    const backButton = root.querySelector('[data-comely-offer-back]');
    const shippingBackButton = root.querySelector('[data-comely-shipping-back]');
    const offersView = root.querySelector('[data-comely-offers-view]');
    const signupView = root.querySelector('[data-comely-signup-view]');
    const shippingView = root.querySelector('[data-comely-shipping-view]');
    const copyButton = root.querySelector('[data-comely-discount-copy]');
    const copyStatus = root.querySelector('[data-comely-discount-copy-status]');
    const codeElement = root.querySelector('[data-comely-discount-code]');
    const result = root.querySelector('[data-comely-discount-result]');
    const openButtonSelector = '[data-comely-discount-open], a[href="#ComelyNewCustomerOffer"]';

    if (!dialog || !closeButton || !offersView || !signupView) return;

    const supportsNativeDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
    let returnFocus = null;
    let copyResetTimer = null;
    let pageState = null;
    let motion = [];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motionTarget = supportsNativeDialog ? dialog : dialog.querySelector('.comely-discount__panel');
    const chatSpring = 'linear(0, .025 2.8%, .101 6.1%, .539 18.9%, .721 25.3%, .849 31.5%, .937 38.1%, .991 45.7%, 1.015 55%, 1.017 63.9%, 1.001)';
    const spring = window.CSS?.supports('animation-timing-function', chatSpring) ? chatSpring : 'cubic-bezier(.23, 1, .32, 1)';
    const cancelMotion = () => {
      motion.forEach(animation => animation.cancel());
      motion = [];
    };
    const restorePosition = () => {
      if (pageState) window.scrollTo({left: pageState.x, top: pageState.y, behavior: 'instant'});
    };
    const preservePage = () => {
      const html = document.documentElement;
      const viewportWidth = html.clientWidth;
      pageState = {x: window.scrollX, y: window.scrollY, width: html.style.width};
      const toolbar = document.querySelector('[data-comely-toolbar]');
      if (toolbar) {
        const rect = toolbar.getBoundingClientRect();
        const computed = getComputedStyle(toolbar);
        const width = parseFloat(computed.width);
        const transform = computed.transform;
        if (width > 0 && rect.width > 0) {
          pageState.toolbar = toolbar;
          pageState.toolbarStyles = {};
          ['width', 'transform', 'transition', 'left', 'bottom'].forEach(name => {pageState.toolbarStyles[name] = toolbar.style[name];});
          toolbar.style.transition = 'none';
          toolbar.style.width = `${width}px`;
          toolbar.style.transform = transform;
          toolbar.style.left = `${rect.left + rect.width / 2}px`;
          toolbar.style.bottom = `${window.innerHeight - rect.bottom}px`;
        }
      }
      html.style.width = `${viewportWidth}px`;
      html.classList.add('comely-discount-open');
    };

    const showOffers = (moveFocus = false) => {
      offersView.hidden = false;
      signupView.hidden = true;
      if (shippingView) shippingView.hidden = true;
      if (moveFocus) offersView.focus({ preventScroll: true });
    };

    const showSignup = (moveFocus = false) => {
      offersView.hidden = true;
      signupView.hidden = false;
      if (shippingView) shippingView.hidden = true;
      if (moveFocus) signupView.focus({ preventScroll: true });
    };

    const showShipping = (moveFocus = false) => {
      if (!shippingView) return;
      offersView.hidden = true;
      signupView.hidden = true;
      shippingView.hidden = false;
      if (moveFocus) shippingView.focus({ preventScroll: true });
    };

    const restoreClosedState = () => {
      if (!pageState) return;
      cancelMotion();
      dialog.classList.remove('is-closing');
      document.documentElement.classList.remove('comely-discount-open');
      document.documentElement.style.width = pageState.width;
      if (pageState.toolbar?.isConnected) {
        Object.entries(pageState.toolbarStyles).forEach(([name,value]) => {pageState.toolbar.style[name] = value;});
      }
      restorePosition();
      const fallbackFocus = document.querySelector(openButtonSelector);
      (returnFocus instanceof HTMLElement && returnFocus.isConnected ? returnFocus : fallbackFocus)?.focus({ preventScroll: true });
      restorePosition();
      pageState = null;
      returnFocus = null;
      if (!result) showOffers();
    };

    const setFallbackOpen = (isOpen) => {
      dialog.classList.toggle('is-fallback-open', isOpen);

      if (isOpen) {
        dialog.setAttribute('open', '');
        dialog.setAttribute('aria-modal', 'true');
        requestAnimationFrame(() => closeButton.focus({ preventScroll: true }));
      } else {
        dialog.removeAttribute('open');
        dialog.removeAttribute('aria-modal');
      }
    };

    const openDialog = (source, initialView = 'offers') => {
      if (dialog.open || document.querySelector('dialog:modal')) return;

      returnFocus = source instanceof HTMLElement ? source : document.activeElement;

      if (initialView === 'signup') showSignup();
      else showOffers();

      preservePage();
      if (supportsNativeDialog) dialog.showModal();
      else setFallbackOpen(true);

      if (!reducedMotion.matches && motionTarget?.animate) {
        motion = [
          motionTarget.animate([{transform:'scale(.2)'},{transform:'scale(1)'}], {duration:320,easing:spring}),
          motionTarget.animate([{opacity:0},{opacity:1}], {duration:150,easing:'cubic-bezier(.25, 0, .1, 1)'})
        ];
      }
      closeButton.focus({preventScroll:true});
      restorePosition();
      requestAnimationFrame(() => {if (dialog.open) restorePosition();});
    };

    const finishClose = () => {
      if (supportsNativeDialog) {
        if (dialog.open) dialog.close();
      } else {
        setFallbackOpen(false);
      }
      restoreClosedState();
    };

    const closeDialog = () => {
      if (!dialog.open || dialog.classList.contains('is-closing')) return;

      if (reducedMotion.matches || !motionTarget?.animate) {
        finishClose();
        return;
      }

      const current = getComputedStyle(motionTarget);
      const from = {transform:current.transform,opacity:current.opacity};
      cancelMotion();
      dialog.classList.add('is-closing');
      const exit = motionTarget.animate([from,{transform:'scale(.2)',opacity:0}], {duration:150,easing:'cubic-bezier(.25, 0, .1, 1)',fill:'forwards'});
      motion = [exit];
      exit.finished.then(() => {if (dialog.classList.contains('is-closing')) finishClose();}).catch(() => {});
    };

    const clearSuccessfulSubmission = () => {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('customer_posted')) return;

      url.searchParams.delete('customer_posted');
      if (url.hash === '#contact_form' || url.hash === '#ComelyDiscountForm') url.hash = '';
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const copyCode = async () => {
      const code = codeElement?.textContent.trim() || root.dataset.discountCode || 'COMELY10';
      let copied = false;

      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch {
        if (codeElement) {
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(codeElement);
          selection.removeAllRanges();
          selection.addRange(range);
          copied = document.execCommand('copy');
          selection.removeAllRanges();
        }
      }

      if (!copied || !copyButton) return;

      const copiedLabel = root.dataset.copiedLabel || 'Copied';
      const copyLabel = root.dataset.copyLabel || 'Copy code';
      copyButton.textContent = copiedLabel;
      if (copyStatus) copyStatus.textContent = copiedLabel;

      clearTimeout(copyResetTimer);
      copyResetTimer = setTimeout(() => {
        copyButton.textContent = copyLabel;
        if (copyStatus) copyStatus.textContent = '';
      }, 1800);
    };

    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest(openButtonSelector) : null;
      if (!button) return;
      event.preventDefault();
      const initialView = button.matches('a[href="#ComelyNewCustomerOffer"]') ? 'signup' : 'offers';
      openDialog(button, initialView);
    });

    claimButton?.addEventListener('click', () => showSignup(true));
    shippingButton?.addEventListener('click', () => showShipping(true));
    backButton?.addEventListener('click', () => showOffers(true));
    shippingBackButton?.addEventListener('click', () => showOffers(true));
    closeButton.addEventListener('click', closeDialog);
    copyButton?.addEventListener('click', copyCode);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });

    dialog.addEventListener('close', restoreClosedState);

    document.addEventListener('keydown', (event) => {
      if (!supportsNativeDialog && dialog.open && event.key === 'Escape') {
        event.preventDefault();
        closeDialog();
      }
    });

    if (result) {
      openDialog(null, 'signup');
      const hasError = result.dataset.comelyDiscountResult === 'error';
      if (!hasError) clearSuccessfulSubmission();

      requestAnimationFrame(() => {
        (hasError ? signupView.querySelector('input[type="email"]') : copyButton)?.focus({ preventScroll: true });
      });
    }
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-comely-discount]').forEach(initializeDiscount);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
